import { isDefined } from "@clipboard-health/util-ts";
import { datadogRum } from "@datadog/browser-rum";
import { ModalStatus, type UseModalState, useModalState } from "@src/lib/ui-react";
import { useCallback, useEffect, useRef, useState } from "react";

const UI_LIFECYCLE_DIAGNOSTIC_ACTION = "HCF Admin Web App: UI lifecycle diagnostic";
const UI_LIFECYCLE_CONSOLE_PREFIX = "[ui-lifecycle-diagnostic]";

let nextInstanceSequence = 0;

export const UI_LIFECYCLE_CLOSE_REASONS = {
  hostOverlayReplacement: "host_overlay_replacement",
  inviteSuccess: "invite_success",
  parentRemount: "parent_remount",
  postSuccess: "post_success",
  queryRefetchReconciliation: "query_refetch_reconciliation",
  routeChange: "route_change",
  submitSuccess: "submit_success",
  submitComplete: "submit_complete",
  userDismiss: "user_dismiss",
} as const;

export type UiLifecycleCloseReason =
  (typeof UI_LIFECYCLE_CLOSE_REASONS)[keyof typeof UI_LIFECYCLE_CLOSE_REASONS];

type UiLifecycleSurfaceKind = "bottom_sheet" | "container" | "dialog" | "flow" | "panel" | "route";
type UiLifecycleContextValue = boolean | number | string | undefined;
type UiLifecycleContext = Record<string, UiLifecycleContextValue>;

interface UseUiLifecycleTelemetryParams {
  context?: UiLifecycleContext;
  isOpen?: boolean;
  surfaceKind: UiLifecycleSurfaceKind;
  surfaceName: string;
}

interface MarkCloseReasonParams {
  reason: UiLifecycleCloseReason;
}

interface UseUiLifecycleTelemetryResult {
  markCloseReason: (params: MarkCloseReasonParams) => void;
}

interface UseUiModalStateParams {
  context?: UiLifecycleContext;
  initialModalStatus?: ModalStatus;
  surfaceKind: "bottom_sheet" | "dialog" | "panel";
  surfaceName: string;
}

interface CloseModalWithReasonParams {
  reason: UiLifecycleCloseReason;
}

export interface UiLifecycleModalState extends UseModalState {
  closeModalWithReason: (params: CloseModalWithReasonParams) => void;
}

interface UiLifecycleEvent {
  closeReason?: UiLifecycleCloseReason;
  eventType:
    | "component_mounted"
    | "component_remounted"
    | "component_unmounted"
    | "surface_closed"
    | "surface_opened";
  instanceId: string;
  previousInstanceId?: string;
  surfaceKind: UiLifecycleSurfaceKind;
  surfaceName: string;
}

interface LastUnmount {
  instanceId: string;
  timestamp: number;
}

const lastUnmountBySurfaceName = new Map<string, LastUnmount>();

export function useUiLifecycleTelemetry(
  params: UseUiLifecycleTelemetryParams
): UseUiLifecycleTelemetryResult {
  const { context, isOpen, surfaceKind, surfaceName } = params;
  const [componentInstanceId] = useState(createInstanceId);
  const lastRenderedRouteRef = useRef(getCurrentRoute());
  const componentMountEmittedRef = useRef(false);
  const surfaceInstanceIdRef = useRef<string | undefined>();
  const openSurfaceContextRef = useRef<UiLifecycleContext | undefined>();
  const latestContextRef = useRef(context);
  const latestIsOpenRef = useRef(isOpen);
  const closeReasonRef = useRef<UiLifecycleCloseReason | undefined>();
  const previousIsOpenRef = useRef(isOpen);

  latestContextRef.current = context;
  latestIsOpenRef.current = isOpen;
  lastRenderedRouteRef.current = getCurrentRoute();
  if (isDefined(isOpen) && isOpen && !isDefined(surfaceInstanceIdRef.current)) {
    surfaceInstanceIdRef.current = createInstanceId();
  }
  if (isDefined(isOpen) && isOpen) {
    openSurfaceContextRef.current = context;
  }
  const instanceId = surfaceInstanceIdRef.current ?? componentInstanceId;

  const markCloseReason = useCallback((markParams: MarkCloseReasonParams) => {
    closeReasonRef.current = markParams.reason;
  }, []);

  const emitComponentMount = useCallback(() => {
    if (componentMountEmittedRef.current) {
      return;
    }
    emitUiLifecycleEvent({
      context: latestContextRef.current,
      event: {
        eventType: "component_mounted",
        instanceId: componentInstanceId,
        surfaceKind,
        surfaceName,
      },
    });
    componentMountEmittedRef.current = true;
  }, [componentInstanceId, surfaceKind, surfaceName]);

  useEffect(() => {
    const tracksRemount = !isDefined(isOpen);
    const lastUnmount = tracksRemount ? lastUnmountBySurfaceName.get(surfaceName) : undefined;
    if (tracksRemount || isOpen) {
      emitComponentMount();
    }

    if (isDefined(lastUnmount)) {
      emitUiLifecycleEvent({
        context: {
          ...latestContextRef.current,
          millisecondsSinceUnmount: Date.now() - lastUnmount.timestamp,
        },
        event: {
          eventType: "component_remounted",
          instanceId: componentInstanceId,
          previousInstanceId: lastUnmount.instanceId,
          surfaceKind,
          surfaceName,
        },
      });
    }

    if (isOpen) {
      emitUiLifecycleEvent({
        context: openSurfaceContextRef.current,
        event: {
          eventType: "surface_opened",
          instanceId,
          surfaceKind,
          surfaceName,
        },
      });
    }

    return () => {
      const inferredUnmountReason = getUnmountReason(lastRenderedRouteRef.current);
      // Cleanup intentionally reads the latest prop-backed refs rather than mount-time values.
      const latestIsOpen = latestIsOpenRef.current;
      const latestContext = latestContextRef.current;
      const latestSurfaceInstanceId = surfaceInstanceIdRef.current;

      if (latestIsOpen) {
        emitUiLifecycleEvent({
          context: openSurfaceContextRef.current,
          event: {
            closeReason: closeReasonRef.current ?? inferredUnmountReason,
            eventType: "surface_closed",
            instanceId: latestSurfaceInstanceId ?? componentInstanceId,
            surfaceKind,
            surfaceName,
          },
        });
      }

      if (componentMountEmittedRef.current) {
        emitUiLifecycleEvent({
          context: latestContext,
          event: {
            closeReason: inferredUnmountReason,
            eventType: "component_unmounted",
            instanceId: componentInstanceId,
            surfaceKind,
            surfaceName,
          },
        });
      }
      if (!isDefined(latestIsOpen)) {
        lastUnmountBySurfaceName.set(surfaceName, {
          instanceId: componentInstanceId,
          timestamp: Date.now(),
        });
      }
    };
    // A lifecycle instance intentionally spans prop/context changes until unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isDefined(isOpen) || previousIsOpenRef.current === isOpen) {
      return;
    }

    if (isOpen) {
      emitComponentMount();
    }
    const eventType = isOpen ? "surface_opened" : "surface_closed";
    emitUiLifecycleEvent({
      context: isOpen ? latestContextRef.current : openSurfaceContextRef.current,
      event: {
        closeReason: isOpen
          ? undefined
          : (closeReasonRef.current ?? UI_LIFECYCLE_CLOSE_REASONS.queryRefetchReconciliation),
        eventType,
        instanceId,
        surfaceKind,
        surfaceName,
      },
    });
    closeReasonRef.current = undefined;
    previousIsOpenRef.current = isOpen;
    if (!isOpen) {
      surfaceInstanceIdRef.current = undefined;
      openSurfaceContextRef.current = undefined;
    }
  }, [emitComponentMount, instanceId, isOpen, surfaceKind, surfaceName]);

  return { markCloseReason };
}

export function useUiModalState(params: UseUiModalStateParams): UiLifecycleModalState {
  const { context, initialModalStatus = ModalStatus.CLOSED, surfaceKind, surfaceName } = params;
  const modalState = useModalState(initialModalStatus);
  const { markCloseReason } = useUiLifecycleTelemetry({
    context,
    isOpen: modalState.modalIsOpen,
    surfaceKind,
    surfaceName,
  });

  function closeModal() {
    if (modalState.modalIsOpen) {
      markCloseReason({ reason: UI_LIFECYCLE_CLOSE_REASONS.userDismiss });
    }
    modalState.closeModal();
  }

  function closeModalWithReason(closeParams: CloseModalWithReasonParams) {
    if (modalState.modalIsOpen) {
      markCloseReason(closeParams);
    }
    modalState.closeModal();
  }

  return {
    ...modalState,
    closeModal,
    closeModalWithReason,
  };
}

interface CloseUiModalWithReasonParams {
  modalState: UseModalState;
  reason: UiLifecycleCloseReason;
}

export function closeUiModalWithReason(params: CloseUiModalWithReasonParams): void {
  const { modalState, reason } = params;
  if (isUiLifecycleModalState(modalState)) {
    modalState.closeModalWithReason({ reason });
    return;
  }
  modalState.closeModal();
}

interface EmitUiLifecycleEventParams {
  context?: UiLifecycleContext;
  event: UiLifecycleEvent;
}

function emitUiLifecycleEvent(params: EmitUiLifecycleEventParams): void {
  const { context, event } = params;
  const payload = {
    ...context,
    ...event,
    diagnosticCategory: "ui_lifecycle",
    errorTrackingEligible: false,
  };

  datadogRum.addAction(UI_LIFECYCLE_DIAGNOSTIC_ACTION, payload);

  if (process.env.REACT_APP_ENVIRONMENT_NAME !== "production") {
    // The LLM reporter currently retains browser warnings/errors only. This warning is a
    // structured diagnostic, never an Error/RUM addError, so it stays out of Error Tracking.
    // eslint-disable-next-line no-console
    console.warn(`${UI_LIFECYCLE_CONSOLE_PREFIX} ${JSON.stringify(payload)}`);
  }
}

function createInstanceId(): string {
  nextInstanceSequence += 1;
  return `ui-${Date.now().toString(36)}-${nextInstanceSequence.toString(36)}`;
}

function isUiLifecycleModalState(modalState: UseModalState): modalState is UiLifecycleModalState {
  return "closeModalWithReason" in modalState;
}

function getCurrentRoute(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function getUnmountReason(initialRoute: string): UiLifecycleCloseReason {
  return getCurrentRoute() === initialRoute
    ? UI_LIFECYCLE_CLOSE_REASONS.parentRemount
    : UI_LIFECYCLE_CLOSE_REASONS.routeChange;
}
